import { gql } from "lib/apolloClient";

export const getOrganizationPointSetQuery = gql`
  query getOrganizationPointSet($id: ID!, $filters: PointAssessmentSetFilter) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        pointAssessmentSets(filters: $filters) {
          totalCount
          edges {
            node {
              id
              associatedEntities {
                id
                ... on AcademicTerm {
                  id
                }
              }
              categories {
                id
                label
                displaySequence
                values {
                  id
                  value
                  genericTag {
                    id
                    label
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const getAssignmentGradingHistoryQuery = gql`
  query getAssignmentGradingHistoryQuery(
    $organizationId: ID!
    $assignmentFilters: AssignmentFeedFilters
    $orderBy: ASSIGNMENT_ORDER_BY_ENUM
    $orderDirection: ORDER_BY_DIRECTION
    $studentIds: [ID!]
    $assignmentAclFilters: ACLControlParameterFilter
    $assessmentToolFilters: AssessmentToolFilters
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        assignments(
          filters: $assignmentFilters
          orderBy: $orderBy
          orderByDirection: $orderDirection
        ) {
          edges {
            node {
              id
              isExcludedFromFinalGradeCalculation
              course {
                id
                learningCourse {
                  id
                  displayName
                }
                aclControlParameters(filters: $assignmentAclFilters) {
                  id
                  permission
                  value
                }
              }
              content {
                id
                ... on Assessment {
                  id
                  weightage
                  assessmentType {
                    id
                    value
                  }
                  assessmentTools(filters: $assessmentToolFilters) {
                    totalCount
                    edges {
                      node {
                        id
                        assessmentToolType
                        assessmentToolItem {
                          id
                          ... on StandardRubric {
                            id
                            associateScoreTool {
                              id
                              scoreToolData {
                                id
                                maxScore
                              }
                            }
                          }
                          ... on ScoreAssessmentTool {
                            id
                            scoreToolData {
                              id
                              maxScore
                            }
                          }
                        }
                      }
                    }
                  }
                  category {
                    id
                    label
                  }
                  globalCategories {
                    id
                    name
                  }
                  academicTerm {
                    id
                    label
                  }
                  title {
                    id
                    value
                  }
                  fields(
                    uids: ["objectivesMYP", "interdisciplinaryCriteriaMYP"]
                  ) {
                    id
                    uid
                    resolvedMinimalTree {
                      ... on ResolvedFieldPlannerElementSet {
                        id
                        nodes {
                          id
                          uid
                          label
                          type
                          depth
                          associatedParents {
                            id
                            ... on Subject {
                              id
                            }
                          }
                        }
                      }
                    }
                  }
                  gradingConfigurations {
                    id
                    settings {
                      id
                      uid
                      value
                    }
                  }
                }
              }
              assignedStudents(studentIds: $studentIds) {
                id
                isEvaluated
                ibPYPElementRatings {
                  id
                  value
                  ibPYPElementId
                  ibPYPElementType
                  parentType
                  academicCriteriaValue {
                    id
                    label
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const getGradingHistoryForLearningGoalsQuery = gql`
  query getGradingHistoryForLearningGoals(
    $id: ID!
    $dataSet: IB_PYP_RATING_DATA_SET_ENUM!
    $curriculumProgramId: ID
    $filtersRatingHistory: ibPYPElementRatingFiltersForRatingHistory
    $postAclFilters: ACLControlParameterFilter
    $assignmentAclFilters: ACLControlParameterFilter
  ) {
    node(id: $id, type: STUDENT) {
      id
      ... on Student {
        ibPYPElementRatings(
          dataSet: $dataSet
          curriculumProgramId: $curriculumProgramId
          filtersForRatingHistory: $filtersRatingHistory
        ) {
          id
          value
          ibPYPElementId
          ibPYPElementType
          parentType
          academicYear {
            id
          }
          academicTerm {
            id
            label
          }
          academicCriteriaValue {
            id
            label
            abbreviation
            color
            description
          }
          evidence {
            id
            metadata
            mimeType
            name
            type
            url
          }
          publishedAt
          createdAt
          createdByV2 {
            id
            firstName
            lastName
            middleName
            type
          }
          description
          parent {
            id
            ... on ImportedEntity {
              id
              type
              name
            }
            ... on Post {
              id
              title
              isPrivate
              isHiddenFromStudents
              attachments {
                id
                title
                type
              }
              course {
                id
                title
                learningCourse {
                  id
                  displayName
                }
                aclControlParameters(filters: $postAclFilters) {
                  id
                  permission
                  value
                }
              }
            }
            ... on StudentProgressReport {
              id
              status
              courseProgressReportTask {
                id
                title
                progressReportTaskGroup {
                  id
                }
                course {
                  id
                  title
                  learningCourse {
                    id
                    displayName
                  }
                }
              }
            }
            ... on StudentAssignment {
              id
              isEvaluated
              isSharedWithStudent
              assignment {
                id
                createdAt
                isExcludedFromFinalGradeCalculation
                course {
                  id
                  title
                  learningCourse {
                    id
                    displayName
                  }
                  aclControlParameters(filters: $assignmentAclFilters) {
                    id
                    permission
                    value
                  }
                }
                content {
                  id
                  ... on Assessment {
                    id
                    assessmentType {
                      id
                      value
                    }
                    academicTerm {
                      id
                      label
                    }
                    title {
                      id
                      value
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const getTermGradeStudentWiseCriteriaQuery = gql`
  query termGradeStudentWiseCriteria(
    $studentId: ID!
    $filters: TermGradeStudentWiseCriteriaFilter
  ) {
    node(id: $studentId, type: STUDENT) {
      id
      ... on Student {
        id
        termGradeStudentWiseCriteria(filters: $filters) {
          id
          student {
            id
          }
          grade {
            id
          }
          criteria {
            id
            label
          }
          academicTerm {
            id
          }
        }
      }
    }
  }
`;

export const getGradeScaleByIdQuery = gql`
  query getGradeScaleById(
    $id: ID!
    $entityDetailsFilters: GradingSetupEntityFilters
  ) {
    node(id: $id, type: GRADE_SCALE) {
      id
      ... on GradeScale {
        id
        displayName
        description
        type
        gradingType
        isArchived
        calculateStandardGradesUsing
        gradeScaleEntityDetails(filters: $entityDetailsFilters) {
          id
          gradeScaleValues {
            id
            abbreviation
            label
            description
            color
          }
        }
      }
    }
  }
`;

export const getStudentAcademicYearsQuery = gql`
  query getStudentAcademicYears($id: ID!, $termFilters: TermFilters) {
    node(id: $id, type: STUDENT) {
      id
      ... on Student {
        id
        academicYears {
          id
          startDate
          endDate
          terms(filters: $termFilters) {
            id
            type
            label
            abbreviation
            lockingStatus
            associatedAcademicTermEntities {
              id
              academicTerm {
                id
                type
              }
              cumulativeAcademicTermWeightages {
                id
                weightage
                percentage
              }
            }
          }
        }
      }
    }
  }
`;
